import detectEthereumProvider from '@metamask/detect-provider'
import MetaMaskOnboarding from '@metamask/onboarding'
import { useCallback, useEffect, useState } from 'react'
import Web3 from 'web3'

export type MetaMaskState = {
  onboarding: () => MetaMaskOnboarding
  isMetaMaskInstalled: () => boolean
  account: string | null
  connect: () => Promise<void>
  web3: Web3 | null
  isLoading: boolean
  currentChainId: string
}

type Options = {
  onConnect?: (web3: Web3) => void
}

export const useMetaMask = (options?: Options): MetaMaskState => {
  const onConnect = options?.onConnect
  const [account, setAccount] = useState<string | null>(null)
  const [web3, setWeb3] = useState<Web3 | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [currentChainId, setChainId] = useState('')

  const isMetaMaskInstalled = useCallback(() => {
    const { ethereum } = window
    return Boolean(ethereum && ethereum.isMetaMask)
  }, [])

  useEffect(() => {
    const onDisconnect = () => {
      window.location.reload()
    }

    const onChangeAccount = (accounts: string[]) => {
      if (accounts.length === 0) {
        onDisconnect()
      }

      setAccount(accounts[0])
    }

    if (isMetaMaskInstalled()) {
      window.ethereum.on('accountsChanged', onChangeAccount)
      return () => {
        window.ethereum.removeListener('accountsChanged', onChangeAccount)
      }
    }
  }, [isMetaMaskInstalled])

  const onChangeChain = useCallback((currentChainId: string) => {
    setChainId(currentChainId)
  }, [])

  useEffect(() => {
    if (isMetaMaskInstalled()) {
      window.ethereum.on('chainChanged', onChangeChain)

      return () => {
        window.ethereum.removeListener('chainChanged', onChangeChain)
      }
    }
  }, [isMetaMaskInstalled, onChangeChain])

  const connect = useCallback(async () => {
    const provider = await detectEthereumProvider()
    if (provider && provider === window.ethereum) {
      const currentChainId = await window.ethereum.request({ method: 'eth_chainId' })
      onChangeChain(currentChainId)

      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' })
      setAccount(accounts[0])

      const web3 = new Web3(window.ethereum)
      setWeb3(web3)

      if (onConnect) {
        onConnect(web3)
      }
    }
  }, [onChangeChain, onConnect])

  useEffect(() => {
    const checkConnection = async () => {
      setIsLoading(true)

      try {
        if (isMetaMaskInstalled()) {
          const permissions = await window.ethereum.request({ method: 'wallet_getPermissions' })
          const capabilities = permissions.map(
            (permission: { parentCapability: string }) => permission.parentCapability
          )
          if (capabilities.includes('eth_accounts')) {
            await connect()
          }
        }
      } catch (e) {
        console.error(e)
      } finally {
        setIsLoading(false)
      }
    }
    checkConnection()
  }, [connect, isMetaMaskInstalled])

  const onboarding = useCallback(() => {
    return new MetaMaskOnboarding()
  }, [])

  return { onboarding, isMetaMaskInstalled, account, connect, web3, isLoading, currentChainId }
}
